import { Tabs } from "antd";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { useTrialsRouter } from "../../../../shared/custom-hooks/useTrialsRouter";
import ProtocolDrawer from "../../../../shared/layout/ProtocolDrawer";
import { ReactComponent as InfoIcon } from "../../../../assets/images/info-14.svg";
import { getTabs, type RatesDosageTabKey } from "../../constants";
import { usePlotSelection } from "../../context/PlotSelectionContext";
import { useNutrientState } from "../../hooks/useNutrientState";
import useNutrientCatalog from "../../hooks/useNutrientCatalog";
import {
  buildNutrientOptions,
  buildNutrientPayload,
  hydrateNutrients,
} from "../../utils/nutrientApi";
import useNutrientMapSync from "../../hooks/useNutrientMapSync";
import { buildNutrientGroups } from "../../utils/nutrientPlots";
import { buildSeedStripes, resolveSeedFlow } from "../../utils/seedFlow";
import { fetchSeedTrial, type SeedTrial } from "../../services/seedAssignments";
import { runAsync } from "../../utils/runAsync";
import {
  getRatesTrialTypesWithData,
  mergeStripPlotsIntoGroup,
  useAgOperations,
  useNewTrialState,
  useUpdateTrial,
} from "../../legacy";
import { Loader } from "../../../../shared/components/Loader";
import { RatesLoadError } from "../RatesLoadError";
import SeedsTab, {
  type BeforeBackSeeds,
  type BeforeLeaveSeeds,
  type ResetSeeds,
} from "../SeedsTab";
import StripeSeedsTab from "../StripeSeedsTab";
import ResetBtn from "../ResetBtn";
import FooterButtons from "../FooterButtons";
import { getSeedPolygonCoordinates } from "../../utils/seedGeometry";
import AxiosInstance from "../../../../shared/utils/axios";

const SEEDS_TAB_KEY = "seeds";

const readStripeStandardMockFlag = (): boolean => {
  if (process.env.NODE_ENV !== "development" || typeof window === "undefined") {
    return false;
  }

  return (
    new URLSearchParams(window.location.search).get("stripeStandardMock") ===
    "1"
  );
};

interface PayloadPlot {
  plot?: {
    id?: number;
    is_strip?: boolean;
    geojson?: {
      geometry?: {
        coordinates?: unknown[];
      };
    };
  };
  properties?: {
    seeds?: {
      rates_and_dosages?: Array<{
        plot_cell_id?: number | null;
      }>;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface SummaryTrial {
  id: string;
  org_id: string;
  property_id: string;
  field_ids: string[];
  is_exportable: boolean;
  trial_plots: ReturnType<
    typeof useNewTrialState
  >["newTrialState"]["trial_plots"];
  strip_design?: { is_strip?: boolean };
}

const assertCompleteSeeds = (trial: SeedTrial): void => {
  const groups = trial.trial_plots ?? [];

  if (groups.length === 0) {
    throw new Error(
      "The trial has no saved plot design. Reload before saving."
    );
  }

  groups.forEach((group, groupIndex) => {
    const expected =
      group.plot?.is_strip === true
        ? 1
        : getSeedPolygonCoordinates(group.plot?.geojson?.geometry).length;

    const entries = group.properties?.seeds?.rates_and_dosages ?? [];
    const cells = entries.map((entry, index) => entry.plot_cell_id ?? index);

    if (
      expected <= 0 ||
      cells.length !== expected ||
      new Set(cells).size !== expected ||
      cells.some(
        (cell) => !Number.isInteger(cell) || cell < 0 || cell >= expected
      )
    ) {
      throw new Error(
        `Finish saving seeds for plot group ${groupIndex + 1} ` +
          "before saving Nutrients. Partial seed assignments " +
          "cannot be sent through the generic trial PATCH."
      );
    }
  });
};

export interface TabsPanelProps {
  showAutoAssignment: boolean;
  hasMockAssignments: boolean;
  onShowAutoAssignment: (show: boolean) => void;
  onCompleteAutoAssignment: () => void;
  onResetAssignments: () => void;
  onManualPreviewChange?: (plotIds: string[]) => void;
  onPlotSelectionActive?: (active: boolean) => void;
}

const TabsPanel = ({
  showAutoAssignment,
  hasMockAssignments,
  onShowAutoAssignment,
  onCompleteAutoAssignment,
  onResetAssignments,
  onManualPreviewChange,
  onPlotSelectionActive,
}: TabsPanelProps): JSX.Element => {
  const { t } = useTranslation();
  const { router, orgId, workspaceId } = useTrialsRouter();

  const [isProtocolDrawerOpen, setIsProtocolDrawerOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<RatesDosageTabKey>(SEEDS_TAB_KEY);
  const [seedsComplete, setSeedsComplete] = useState(false);
  const [seedsSaving, setSeedsSaving] = useState(false);
  const [pendingSeedsContinuation, setPendingSeedsContinuation] =
    useState(false);
  const [summarySaving, setSummarySaving] = useState(false);
  const [biologicalsComplete, setBiologicalsComplete] = useState(false);
  const [biologicalsTabComplete, setBiologicalsTabComplete] = useState(false);
  const [biologicalsSaving, setBiologicalsSaving] = useState(false);
  const [biologicalsResetAvailable, setBiologicalsResetAvailable] =
    useState(false);
  const resetBiologicals = useRef<(() => Promise<boolean>) | null>(null);
  const registerResetBiologicals = useCallback(
    (handler: (() => Promise<boolean>) | null): void => {
      resetBiologicals.current = handler;
    },
    []
  );
  const [seedsResetAvailable, setSeedsResetAvailable] = useState(false);
  const [seedResetRevision, setSeedResetRevision] = useState(0);
  const [stripeStandardMockEnabled] = useState(readStripeStandardMockFlag);
  const [activeApplicationId, setActiveApplicationId] = useState(1);

  const { setActiveScope, session } = usePlotSelection();
  const biologicalSelectionActive = session?.tab === "biologicals";
  const {
    newTrialState,
    trialInfo,
    trialProtocolData,
    setTrialInfo,
    setNewTrialState,
  } = useNewTrialState();
  const updateTrial = useUpdateTrial();

  const { handleAgOperation } = useAgOperations();

  const saving = useRef(false);
  const seedTransition = useRef(false);
  const beforeLeaveSeeds = useRef<BeforeLeaveSeeds | null>(null);
  const beforeBackSeeds = useRef<BeforeBackSeeds | null>(null);
  const resetSeeds = useRef<ResetSeeds | null>(null);
  const beforeLeaveBiologicals = useRef<(() => Promise<boolean>) | null>(null);
  const registerBeforeLeaveBiologicals = useCallback(
    (handler: (() => Promise<boolean>) | null): void => {
      beforeLeaveBiologicals.current = handler;
    },
    []
  );

  const registerBeforeLeaveSeeds = useCallback(
    (handler: BeforeLeaveSeeds | null): void => {
      beforeLeaveSeeds.current = handler;
    },
    []
  );

  const registerBeforeBackSeeds = useCallback(
    (handler: BeforeBackSeeds | null): void => {
      beforeBackSeeds.current = handler;
    },
    []
  );

  const registerResetSeeds = useCallback((handler: ResetSeeds | null): void => {
    resetSeeds.current = handler;
  }, []);

  const hasNutrients = Boolean(trialProtocolData?.product_details?.nutrients);
  const hasSeeds = Boolean(trialProtocolData?.product_details?.seeds);
  const hasBiologicals = Boolean(
    trialProtocolData?.product_details?.biologicals
  );

  const nutrientCatalog = useNutrientCatalog(
    hasNutrients ? trialProtocolData?.org_id ?? orgId : undefined,
    trialProtocolData?.crop_details?.country
  );

  const seedDesign = useMemo(
    () => ({
      trial_plots: newTrialState?.trial_plots,
      stripsData: newTrialState?.stripsData,
    }),
    [newTrialState?.trial_plots, newTrialState?.stripsData]
  );

  const isStripMode = resolveSeedFlow(seedDesign) === "stripes";

  const stripes = useMemo(
    () =>
      buildSeedStripes(seedDesign).map((stripe, index) => ({
        ...stripe,
        label: formatTranslation(
          t("newRatesAndDosage.seeds.stripeLabel", {
            index: index + 1,
          })
        ),
      })),
    [seedDesign, t]
  );

  const stripeIdentityKey = JSON.stringify(stripes.map((stripe) => stripe.id));

  const seedComponentKey = [
    trialInfo?.id ?? "trial",
    isStripMode ? "stripes" : "plots",
    isStripMode ? stripeIdentityKey : "",
    seedResetRevision,
  ].join("-");

  const nutrientGroups = useMemo(
    () =>
      buildNutrientGroups(
        newTrialState?.trial_plots,
        newTrialState?.stripsData
      ),
    [newTrialState?.trial_plots, newTrialState?.stripsData]
  );

  const nutrientCode =
    trialProtocolData?.product_details?.nutrients?.products?.[0]
      ?.nutrient_code ?? "N";

  const nutrientOptions = useMemo(
    () =>
      buildNutrientOptions(
        nutrientCatalog.data ?? [],
        trialInfo?.standard_field_properties?.standard_fertilisers ?? [],
        nutrientCode
      ),
    [
      nutrientCatalog.data,
      trialInfo?.standard_field_properties?.standard_fertilisers,
      nutrientCode,
    ]
  );

  const savedNutrients = useMemo(
    () => ({
      ...hydrateNutrients(trialInfo ?? {}, nutrientGroups),
      protocolId: trialInfo?.protocol_id,
      draftStorageKey:
        hasNutrients && trialInfo?.id
          ? `rates-dosage:nutrients:${orgId ?? ""}:${
              workspaceId ?? ""
            }:${String(trialInfo.id)}`
          : undefined,
    }),
    [trialInfo, nutrientGroups, hasNutrients, orgId, workspaceId]
  );

  const nutrientDraft = useNutrientState(
    isStripMode ? "stripes" : "plots",
    nutrientGroups,
    undefined,
    trialProtocolData?.product_details?.nutrients,
    nutrientOptions,
    savedNutrients,
    nutrientCode
  );

  const nutrientState = {
    ...nutrientDraft,
    nutrientCode,
  };

  const completed: Record<RatesDosageTabKey, boolean> = {
    seeds: seedsComplete,
    biologicals: biologicalsComplete,
    nutrient: nutrientState.isComplete && !nutrientState.isAutoAssigning,
  };

  const canSaveNutrients =
    Boolean(trialInfo?.id) &&
    !nutrientCatalog.isFetching &&
    !nutrientCatalog.isError;

  const availableTabKeys: RatesDosageTabKey[] = [
    ...(hasSeeds ? ["seeds" as const] : []),
    ...(trialProtocolData?.product_details?.biologicals
      ? ["biologicals" as const]
      : []),
    ...(hasNutrients ? ["nutrient" as const] : []),
  ];

  const resolvedActiveKey = availableTabKeys.includes(activeKey)
    ? activeKey
    : availableTabKeys[0];

  const saveSeedsBeforeNavigation = async (
    createAgOperation = false
  ): Promise<boolean> => {
    if (!hasSeeds) return true;

    const handler = beforeLeaveSeeds.current;

    if (handler === null) {
      toast.error("Wait for the Seeds tab to finish loading.");
      return false;
    }

    return await handler(createAgOperation);
  };

  const handleSummary = async (
    biologicalsSaved = false,
    createSeedAgOperation = false
  ): Promise<void> => {
    if (
      summaryDisabled ||
      seedsSaving ||
      saving.current ||
      seedTransition.current ||
      !trialInfo?.id
    ) {
      return;
    }

    saving.current = true;
    setSummarySaving(true);

    try {
      if (!(await saveSeedsBeforeNavigation(createSeedAgOperation))) {
        if (hasSeeds) setActiveKey(SEEDS_TAB_KEY);
        return;
      }
      if (hasBiologicals && !biologicalsSaved) {
        if (!beforeLeaveBiologicals.current) {
          throw new Error(
            "Wait for the CP & Biologicals tab to finish loading."
          );
        }
        if (!(await beforeLeaveBiologicals.current())) return;
      }

      const trialId = trialInfo.id;

      if (!hasNutrients && !hasBiologicals) {
        router.goToTrialsEditPage(undefined, trialId, undefined, true);
        return;
      }

      const payload: Record<string, unknown> = hasNutrients
        ? buildNutrientPayload(
            newTrialState,
            trialInfo,
            trialProtocolData,
            nutrientState,
            nutrientOptions
          )
        : {
            completed_step: Math.max(trialInfo.completed_step ?? 0, 3),
            completion: false,
          };

      // Preserve authoritative seed assignments during a later whole-array
      // nutrient save. This also covers per-strip seed assignments.
      if (hasNutrients && hasSeeds) {
        const fresh = await fetchSeedTrial(trialId, {
          orgId: orgId ?? "",
          workspaceId: workspaceId ?? "",
        });

        assertCompleteSeeds(fresh);

        const payloadPlots = payload.trial_plots as PayloadPlot[];
        const savedPlots = fresh.trial_plots ?? [];

        if (payloadPlots.length !== savedPlots.length) {
          throw new Error("The plot design changed. Reload before saving.");
        }

        payloadPlots.forEach((plot, index) => {
          const outgoing = plot.plot?.geojson?.geometry?.coordinates;
          const stored =
            savedPlots[index]?.plot?.geojson?.geometry?.coordinates;

          if (
            outgoing === undefined ||
            stored === undefined ||
            JSON.stringify(outgoing) !== JSON.stringify(stored)
          ) {
            throw new Error(
              "The saved plot geometry or cell order changed. " +
                "Reload before saving."
            );
          }
        });

        payload.trial_plots = payloadPlots.map((plot, index) => ({
          ...plot,
          properties: {
            ...plot.properties,
            seeds: savedPlots[index].properties?.seeds,
          },
        }));

        payload.standard_field_properties = {
          ...fresh.standard_field_properties,
          ...(payload.standard_field_properties as Record<string, unknown>),
          standard_seed_product_id:
            fresh.standard_field_properties?.standard_seed_product_id,
          standard_product: fresh.standard_field_properties?.standard_product,
          standard_seed_rate:
            fresh.standard_field_properties?.standard_seed_rate,
        };
      }

      if (hasNutrients && hasBiologicals) {
        const { data: fresh } = await AxiosInstance.get<{
          trial_plots: PayloadPlot[];
          standard_field_properties?: { standard_tank_mix_rates?: number[] };
        }>(`/trial/${String(trialId)}`);
        const payloadPlots = payload.trial_plots as PayloadPlot[];
        if (
          !Array.isArray(fresh.trial_plots) ||
          fresh.trial_plots.length !== payloadPlots.length ||
          payloadPlots.some(
            (plot, index) =>
              JSON.stringify(plot.plot?.geojson?.geometry?.coordinates) !==
              JSON.stringify(
                fresh.trial_plots[index].plot?.geojson?.geometry?.coordinates
              )
          )
        ) {
          throw new Error(
            "The biological plot design changed. Reload before saving."
          );
        }
        // The nutrient draft predates the biological PATCH; never send its stale CP data.
        payload.trial_plots = payloadPlots.map((plot, index) => ({
          ...plot,
          properties: {
            ...plot.properties,
            biologicals: fresh.trial_plots[index].properties?.biologicals,
          },
        }));
        payload.standard_field_properties = {
          ...(payload.standard_field_properties as Record<string, unknown>),
          standard_tank_mix_rates:
            fresh.standard_field_properties?.standard_tank_mix_rates,
        };
      }

      const updatedTrial = await updateTrial.mutateAsync({
        trialId,
        payload,
      });

      if (!updatedTrial) {
        throw new Error("The Rates & Dosage save returned no trial.");
      }

      const { data: summaryTrial } = await AxiosInstance.get<SummaryTrial>(
        `/trial/${String(trialId)}`
      );
      if (
        summaryTrial?.id !== trialId ||
        !Array.isArray(summaryTrial.trial_plots)
      ) {
        throw new Error(
          "The Summary trial response is incomplete. Retry saving."
        );
      }
      const summaryPlots: typeof newTrialState.trial_plots =
        mergeStripPlotsIntoGroup(
          summaryTrial.trial_plots,
          summaryTrial.strip_design?.is_strip === true
        );
      if (summaryPlots.length !== newTrialState.trial_plots.length) {
        throw new Error(
          "The plot design changed. Reload before opening Summary."
        );
      }

      if (summaryTrial.is_exportable) {
        await handleAgOperation(
          summaryTrial,
          getRatesTrialTypesWithData(
            summaryTrial.trial_plots,
            (
              trialProtocolData?.protocol_type ?? [
                ...(hasSeeds ? ["seed"] : []),
                ...(hasBiologicals ? ["biological"] : []),
                ...(hasNutrients ? ["nutrient"] : []),
              ]
            ).filter(
              (trialType): trialType is "seed" | "nutrient" | "biological" =>
                trialType === "seed" ||
                trialType === "nutrient" ||
                trialType === "biological"
            )
          )
        );
      }

      setTrialInfo({ ...summaryTrial, trial_plots: summaryPlots });
      // Summary prefers the live plot state, so refresh its saved properties too.
      setNewTrialState((previous: typeof newTrialState) => ({
        ...previous,
        trial_plots: previous.trial_plots.map((plot, index) => ({
          ...plot,
          properties: summaryPlots[index].properties,
        })),
      }));
      router.goToTrialsEditPage(undefined, trialId, undefined, true);
    } catch (error) {
      console.error("Failed to save Rates & Dosage before Summary:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : formatTranslation(t("common.error.update"))
      );
    } finally {
      saving.current = false;
      setSummarySaving(false);
    }
  };

  const handleContinueFromBiologicals = (): void => {
    if (hasNutrients) {
      setActiveKey("nutrient");
      return;
    }

    runAsync(handleSummary(true));
  };

  const tabs = getTabs({
    newTrialState,
    trialInfo,
    trialProtocolData,
    nutrientState,
    completed,
    biologicalsTabComplete,
    onBiologicalsCompletionChange: setBiologicalsTabComplete,
    onContinueFromBiologicals: handleContinueFromBiologicals,
    onNavigateToSeeds: hasSeeds
      ? () => {
          setActiveKey(SEEDS_TAB_KEY);
        }
      : undefined,
    onNavigateToPlotDesign: () => {
      runAsync(navigateToPlotDesign());
    },
    onPlotSelectionActive,
    onBiologicalsValidationChange: setBiologicalsComplete,
    onApplicationChange: setActiveApplicationId,
    active: resolvedActiveKey === "biologicals",
    onRegisterBeforeLeave: registerBeforeLeaveBiologicals,
    onSavingChange: setBiologicalsSaving,
    onRegisterReset: registerResetBiologicals,
    onResetAvailabilityChange: setBiologicalsResetAvailable,
  });

  const nextSeedsTab = tabs[1];
  const seedsNextLabel = nextSeedsTab
    ? formatTranslation(t(`newRatesAndDosage.tabs.${nextSeedsTab.key}`))
    : formatTranslation(t("newRatesAndDosage.footer.summary"));

  const allTabsComplete =
    tabs.length > 0 &&
    tabs.every((tab) => completed[tab.key as RatesDosageTabKey]);

  const summaryDisabled =
    !allTabsComplete ||
    seedsSaving ||
    biologicalsSaving ||
    summarySaving ||
    updateTrial.isPending ||
    (hasNutrients && (!canSaveNutrients || !nutrientState.isComplete));

  const visibleActiveKey = tabs.some((tab) => tab.key === activeKey)
    ? activeKey
    : (tabs[0]?.key as RatesDosageTabKey | undefined);

  const isSeedsActive = visibleActiveKey === SEEDS_TAB_KEY;
  const isBiologicalsActive = visibleActiveKey === "biologicals";
  const isNutrientActive = visibleActiveKey === "nutrient";
  const selectionTargetMode = isStripMode ? "stripe" : "plot";

  useLayoutEffect(() => {
    if (!visibleActiveKey) {
      setActiveScope(null);
      return;
    }

    setActiveScope({
      domain: visibleActiveKey,
      ...(isBiologicalsActive ? { applicationId: activeApplicationId } : {}),
      targetMode: selectionTargetMode,
      trialId: trialInfo?.id,
    });
  }, [
    activeApplicationId,
    isBiologicalsActive,
    selectionTargetMode,
    setActiveScope,
    trialInfo?.id,
    visibleActiveKey,
  ]);

  useNutrientMapSync(nutrientState, isNutrientActive);

  const changeTab = async (
    nextKey: RatesDosageTabKey,
    createSeedAgOperation = false
  ): Promise<void> => {
    if (
      biologicalSelectionActive ||
      nextKey === visibleActiveKey ||
      seedsSaving ||
      biologicalsSaving ||
      summarySaving ||
      saving.current ||
      seedTransition.current ||
      updateTrial.isPending
    ) {
      return;
    }

    if (!isSeedsActive) {
      if (isBiologicalsActive && nextKey === "nutrient") {
        if (!beforeLeaveBiologicals.current) {
          toast.error("Wait for the CP & Biologicals tab to finish loading.");
          return;
        }
        if (!(await beforeLeaveBiologicals.current())) return;
      }
      setActiveKey(nextKey);
      return;
    }

    seedTransition.current = true;

    try {
      if (await saveSeedsBeforeNavigation(createSeedAgOperation)) {
        setActiveKey(nextKey);
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : formatTranslation(t("common.error.update"))
      );
    } finally {
      seedTransition.current = false;
    }
  };

  const handleContinueFromSeeds = useCallback((): void => {
    setPendingSeedsContinuation(true);
  }, []);

  // Auto-assignment calls Continue immediately after saving. Wait for the
  // child's busy/validation effects and registered leave-check to settle before
  // using the parent's navigation guards; the save's render can still be busy.
  useEffect(() => {
    if (!pendingSeedsContinuation || seedsSaving) {
      return;
    }

    setPendingSeedsContinuation(false);
    if (!isSeedsActive) return;

    const nextTab = tabs[1];

    if (nextTab) {
      runAsync(changeTab(nextTab.key as RatesDosageTabKey, true));
      return;
    }

    runAsync(handleSummary(false, true));
  }, [
    changeTab,
    handleSummary,
    isSeedsActive,
    pendingSeedsContinuation,
    seedsSaving,
    tabs,
  ]);

  const handleResetSeeds = async (): Promise<void> => {
    const reset = resetSeeds.current;

    if (
      reset === null ||
      !seedsResetAvailable ||
      seedsSaving ||
      summarySaving ||
      saving.current ||
      seedTransition.current
    ) {
      return;
    }

    seedTransition.current = true;
    setSeedsSaving(true);

    try {
      if (await reset()) {
        setSeedResetRevision((previous) => previous + 1);
        setSeedsComplete(false);
        setSeedsResetAvailable(false);
        onResetAssignments();
      }
    } finally {
      seedTransition.current = false;
      setSeedsSaving(false);
    }
  };

  const seedsResetDisabled = !seedsResetAvailable;
  const handleResetBiologicals = async (): Promise<void> => {
    if (
      biologicalsSaving ||
      summarySaving ||
      saving.current ||
      !biologicalsResetAvailable
    )
      return;
    await resetBiologicals.current?.();
  };

  const resetDisabled = isSeedsActive
    ? seedsResetDisabled || seedsSaving
    : isBiologicalsActive
    ? !biologicalsResetAvailable || biologicalsSaving
    : !isNutrientActive || !nutrientState.canReset;

  const navigateToPlotDesign = async (): Promise<void> => {
    if (isSeedsActive) {
      if (
        seedsSaving ||
        summarySaving ||
        seedTransition.current ||
        updateTrial.isPending
      ) {
        return;
      }

      const handler = beforeBackSeeds.current;
      if (handler === null) {
        toast.error("Wait for the Seeds tab to finish loading.");
        return;
      }

      seedTransition.current = true;
      try {
        if (!(await handler())) return;
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : formatTranslation(t("common.error.update"))
        );
        return;
      } finally {
        seedTransition.current = false;
      }
    }

    router.goToTrialsEditPage(
      undefined,
      trialInfo?.id,
      undefined,
      undefined,
      true
    );
  };

  const items = tabs.map((item) =>
    item.key === SEEDS_TAB_KEY
      ? {
          ...item,
          children: isStripMode ? (
            <StripeSeedsTab
              key={seedComponentKey}
              stripes={stripes}
              active={isSeedsActive}
              screenshotMock={
                stripeStandardMockEnabled && seedResetRevision === 0
              }
              onBack={() => {
                runAsync(navigateToPlotDesign());
              }}
              onContinue={handleContinueFromSeeds}
              nextLabel={seedsNextLabel}
              onValidationChange={setSeedsComplete}
              onDirtyChange={() => {}}
              onSavingChange={setSeedsSaving}
              onRegisterBeforeLeave={registerBeforeLeaveSeeds}
              onRegisterBeforeBack={registerBeforeBackSeeds}
              onRegisterReset={registerResetSeeds}
              onResetAvailabilityChange={setSeedsResetAvailable}
              onManualPreviewChange={onManualPreviewChange}
            />
          ) : (
            <SeedsTab
              key={seedComponentKey}
              active={isSeedsActive}
              showAutoAssignment={showAutoAssignment}
              hasMockAssignments={hasMockAssignments}
              onShowAutoAssignment={onShowAutoAssignment}
              onCompleteAutoAssignment={onCompleteAutoAssignment}
              onManualPreviewChange={onManualPreviewChange}
              onValidationChange={setSeedsComplete}
              onSavingChange={setSeedsSaving}
              onRegisterBeforeLeave={registerBeforeLeaveSeeds}
              onRegisterBeforeBack={registerBeforeBackSeeds}
              onRegisterReset={registerResetSeeds}
              onResetAvailabilityChange={setSeedsResetAvailable}
              onContinue={handleContinueFromSeeds}
              nextLabel={seedsNextLabel}
            />
          ),
        }
      : item
  );

  const showSharedFooter =
    !isBiologicalsActive &&
    !(isSeedsActive && (isStripMode || showAutoAssignment));
  const previousNutrientTab = isNutrientActive
    ? availableTabKeys[availableTabKeys.indexOf("nutrient") - 1]
    : undefined;

  const handleBack = (): void => {
    if (
      seedsSaving ||
      summarySaving ||
      saving.current ||
      seedTransition.current ||
      updateTrial.isPending
    ) {
      return;
    }

    if (previousNutrientTab) {
      runAsync(changeTab(previousNutrientTab));
      return;
    }

    runAsync(navigateToPlotDesign());
  };

  const sharedFooter = showSharedFooter ? (
    <FooterButtons
      onBack={handleBack}
      backLabel={
        previousNutrientTab
          ? formatTranslation(
              t(`newRatesAndDosage.tabs.${previousNutrientTab}`)
            )
          : undefined
      }
      onNext={() => {
        if (isSeedsActive) {
          handleContinueFromSeeds();
          return;
        }

        runAsync(handleSummary());
      }}
      nextLabel={isSeedsActive ? seedsNextLabel : undefined}
      loading={seedsSaving || summarySaving || updateTrial.isPending}
      nextDisabled={
        isSeedsActive
          ? !seedsComplete || seedsSaving || updateTrial.isPending
          : summaryDisabled
      }
    />
  ) : null;

  return (
    <div
      className={`rates-dosage-main rd-tabs-panel${
        isSeedsActive ? " rd-tabs-panel--seeds" : ""
      }`}
    >
      {(seedsSaving ||
        summarySaving ||
        updateTrial.isPending ||
        (isNutrientActive && nutrientCatalog.isFetching)) && (
        <Loader mask message="" />
      )}

      <div className="title-sec">
        <h3 className="main-title" data-testid="new-rates-dosage-title">
          {formatTranslation(t("newRatesAndDosage.title"))}
        </h3>

        <ResetBtn
          disabled={
            biologicalSelectionActive ||
            resetDisabled ||
            summarySaving ||
            updateTrial.isPending
          }
          onClick={
            isSeedsActive
              ? handleResetSeeds
              : isBiologicalsActive
              ? handleResetBiologicals
              : nutrientState.reset
          }
        />
      </div>

      <Tabs
        className="rd-tabs-panel__tabs"
        activeKey={visibleActiveKey}
        onChange={(key) => {
          runAsync(changeTab(key as RatesDosageTabKey));
        }}
        items={items.map((item) => ({
          ...item,
          disabled: biologicalSelectionActive || item.disabled,
          children:
            item.key === SEEDS_TAB_KEY ? (
              <>
                {item.children}
                {isSeedsActive && sharedFooter}
              </>
            ) : (
              item.children
            ),
        }))}
        tabBarExtraContent={
          <button
            type="button"
            className="rd-tabs-panel__protocol-info"
            data-testid="new-rates-dosage-protocol-info"
            disabled={biologicalSelectionActive}
            onClick={() => {
              setIsProtocolDrawerOpen(true);
            }}
          >
            <InfoIcon aria-hidden />
            <span>
              {formatTranslation(t("newRatesAndDosage.protocolInfo"))}
            </span>
          </button>
        }
        data-testid="new-rates-dosage-tabs"
      />

      {isNutrientActive && nutrientCatalog.isError && (
        <RatesLoadError
          onRetry={() => {
            runAsync(nutrientCatalog.refetch());
          }}
          isRetrying={nutrientCatalog.isFetching}
        />
      )}

      <ProtocolDrawer
        open={isProtocolDrawerOpen}
        onClose={() => {
          setIsProtocolDrawerOpen(false);
        }}
        protocolId={trialInfo?.protocol_id ?? trialProtocolData?.id ?? ""}
        extraHeaders={{
          "org-id": orgId ?? "",
          "workspace-id": workspaceId ?? "",
        }}
      />

      {!isSeedsActive && sharedFooter}
    </div>
  );
};

export default TabsPanel;
